import { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';

// Generic fetch hook
export const useFetch = (url, deps = []) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetch = useCallback(async () => {
    if (!url) return;
    setLoading(true);
    try {
      const { data: res } = await api.get(url);
      setData(res);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load');
    } finally { setLoading(false); }
  }, [url]);

  useEffect(() => { fetch(); }, [fetch, ...deps]);
  return { data, loading, error, refetch: fetch };
};

// Classes hook
export const useClasses = () => {
  const { data: classes, loading, refetch } = useFetch('/classes');
  return { classes: classes || [], loading, refetch };
};

// Students hook with filters
export const useStudents = (filters = {}) => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams(filters).toString();
      const { data } = await api.get(`/students?${params}`);
      setStudents(data.data || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast.error('Failed to load students');
    } finally { setLoading(false); }
  }, [JSON.stringify(filters)]);

  useEffect(() => { fetch(); }, [fetch]);
  return { students, loading, total, refetch: fetch };
};

// Generic submit hook
export const useSubmit = () => {
  const [loading, setLoading] = useState(false);

  const submit = async (fn, successMsg) => {
    setLoading(true);
    try {
      const result = await fn();
      if (successMsg) toast.success(successMsg);
      return { success: true, data: result };
    } catch (err) {
      const msg = err.response?.data?.error || 'Something went wrong';
      toast.error(msg);
      return { success: false, error: msg };
    } finally { setLoading(false); }
  };

  return { loading, submit };
};

// Dashboard hook
export const useDashboard = () => {
  const { data, loading, refetch } = useFetch('/dashboard');
  return { stats: data, loading, refetch };
};

// Attendance hook
export const useAttendance = (classId, date) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetch = useCallback(async () => {
    if (!classId || !date) return;
    setLoading(true);
    try {
      const { data: res } = await api.get(`/attendance?class_id=${classId}&date=${date}`);
      setData(res);
    } catch { toast.error('Failed to load attendance'); }
    finally { setLoading(false); }
  }, [classId, date]);

  useEffect(() => { fetch(); }, [fetch]);
  return { data, loading, refetch: fetch };
};

// WhatsApp logs hook
export const useWhatsAppLogs = () => useFetch('/whatsapp/logs');
